import React, { useState } from "react";
import PropTypes from "prop-types";
import { Button, Modal } from "react-bootstrap";
import { getCSRFToken } from "views_shared/app/javascript/api/withTokenRefresh";

import ScheduleStoryIdeaModal from "./ScheduleStoryIdeaModal";

// StoryIdeaActions — context-appropriate row actions for a StoryIdea
// in the publication lifecycle (#3121).
//
// State machine surface:
//   draft     → Edit, Schedule (opens modal), Delete
//   scheduled → Revert to Draft, Delete (no Edit; explicit revert first)
//   published → read-only (no actions)
//
// Each mutating action calls back to onMutate when it completes so the
// table can refresh. Errors are surfaced inline via the supplied
// onError callback (parent owns the toast/alert UI).

// Admin endpoints are Rails routes (not PostgREST), so they need the
// Rails CSRF token from the meta tag — not the Supabase JWT that
// authenticatedHeadersHash provides for the API surface.
function railsHeaders() {
  return {
    "Content-Type": "application/json",
    Accept: "application/json",
    "X-CSRF-Token": getCSRFToken(),
  };
}

function postWithCSRF(path, body) {
  return fetch(path, {
    method: "POST",
    headers: railsHeaders(),
    body: body ? JSON.stringify(body) : undefined,
  });
}

function deleteRequest(path) {
  return fetch(path, {
    method: "DELETE",
    headers: railsHeaders(),
  });
}

async function readErrorMessage(resp, fallback) {
  try {
    const body = await resp.json();
    return body.error || body.errors?.join(", ") || fallback;
  } catch (_) {
    return fallback;
  }
}

function StoryIdeaActions({ row, onMutate, onError }) {
  const [busy, setBusy] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);

  const reportError = (msg) => {
    if (onError) onError(msg);
  };

  const handleSchedule = async (params) => {
    setBusy(true);
    try {
      const resp = await postWithCSRF(`/admin/story_ideas/${row.id}/schedule`, params);
      if (!resp.ok) {
        reportError(await readErrorMessage(resp, `Schedule failed (HTTP ${resp.status})`));
        return;
      }
      setShowScheduleModal(false);
      if (onMutate) onMutate();
    } finally {
      setBusy(false);
    }
  };

  const handleRevert = async () => {
    setBusy(true);
    try {
      const resp = await postWithCSRF(`/admin/story_ideas/${row.id}/revert_to_draft`);
      if (!resp.ok) {
        reportError(await readErrorMessage(resp, `Revert failed (HTTP ${resp.status})`));
        return;
      }
      // Clicking Revert to Draft signals the admin wants to change
      // something — take them straight to the edit form rather than
      // dropping them back on the list. The form pre-populates from
      // the just-reverted row, so the edit can start immediately.
      window.location.href = `/admin/story_ideas/${row.id}/edit`;
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteConfirmed = async () => {
    setShowDeleteConfirm(false);
    setBusy(true);
    try {
      const resp = await deleteRequest(`/admin/story_ideas/${row.id}`);
      if (!resp.ok) {
        reportError(await readErrorMessage(resp, `Delete failed (HTTP ${resp.status})`));
        return;
      }
      if (onMutate) onMutate();
    } finally {
      setBusy(false);
    }
  };

  // Published: no actions.
  if (row.status === "published") {
    return null;
  }

  // Scheduled: Revert and Delete only. No Edit — admins must revert
  // first; the deliberate friction stops anyone editing a row whose
  // publish job is mid-countdown. Layout matches the Draft row below
  // (flex + gap-2) so the buttons line up identically across statuses.
  if (row.status === "scheduled") {
    return (
      <>
        <div className="d-flex gap-2 align-items-center">
          <Button
            variant="outline-secondary"
            size="sm"
            disabled={busy}
            onClick={handleRevert}
            title="Cancel the schedule and return to draft. Any pending publish job will no-op."
          >
            <i className="fa-solid fa-rotate-left me-1" aria-hidden="true" />
            Revert to Draft
          </Button>
          <Button variant="outline-danger" size="sm" disabled={busy} onClick={() => setShowDeleteConfirm(true)}>
            <i className="fa-solid fa-trash" aria-hidden="true" />
          </Button>
        </div>
        <DeleteConfirmModal
          show={showDeleteConfirm}
          row={row}
          onCancel={() => setShowDeleteConfirm(false)}
          onConfirm={handleDeleteConfirmed}
        />
      </>
    );
  }

  // Draft (default): Edit, Schedule, Delete.
  return (
    <>
      <div className="d-flex gap-2 align-items-center">
        <Button as="a" href={`/admin/story_ideas/${row.id}/edit`} variant="outline-secondary" size="sm">
          <i className="fa-solid fa-pen me-1" aria-hidden="true" />
          Edit
        </Button>
        <Button variant="primary" size="sm" disabled={busy} onClick={() => setShowScheduleModal(true)}>
          <i className="fa-solid fa-paper-plane me-1" aria-hidden="true" />
          Schedule
        </Button>
        <Button variant="outline-danger" size="sm" disabled={busy} onClick={() => setShowDeleteConfirm(true)}>
          <i className="fa-solid fa-trash" aria-hidden="true" />
        </Button>
      </div>
      <ScheduleStoryIdeaModal
        show={showScheduleModal}
        onHide={() => setShowScheduleModal(false)}
        onSchedule={handleSchedule}
        storyIdeaTitle={row.title}
        previousPublishAt={row.previous_publish_at}
        busy={busy}
      />
      <DeleteConfirmModal
        show={showDeleteConfirm}
        row={row}
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteConfirmed}
      />
    </>
  );
}

function DeleteConfirmModal({ show, row, onCancel, onConfirm }) {
  return (
    <Modal show={show} onHide={onCancel}>
      <Modal.Header closeButton>
        <Modal.Title>Delete story idea?</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>
          Delete <strong>{row.title}</strong>? This is irreversible.
        </p>
        {row.status === "scheduled" && (
          <p className="text-warning mb-0">
            <i className="fa-solid fa-triangle-exclamation me-1" aria-hidden="true" />
            This story idea is currently scheduled. Deleting it cancels the schedule.
          </p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="danger" onClick={onConfirm}>
          Delete
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

DeleteConfirmModal.propTypes = {
  show: PropTypes.bool.isRequired,
  row: PropTypes.shape({
    title: PropTypes.string,
    status: PropTypes.string,
  }).isRequired,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};

StoryIdeaActions.propTypes = {
  row: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    title: PropTypes.string,
    status: PropTypes.string.isRequired,
    publish_at: PropTypes.string,
    previous_publish_at: PropTypes.string,
  }).isRequired,
  // Called after a successful mutation so the parent can refresh.
  onMutate: PropTypes.func,
  // Called with a user-facing string for failed mutations.
  onError: PropTypes.func,
};

StoryIdeaActions.defaultProps = {
  onMutate: null,
  onError: null,
};

export default StoryIdeaActions;
